/**
 * The Scans dashboard at `/scans` (DEC-191 decision 1, UI_SDD §R.5), the sidebar's
 * "Scans". It replaces the sidebar's Recent scans and the New scan page's Past
 * evaluations list; New scan keeps its own page at `/connect`.
 *
 * - Four clickable numbers are the status filter. There is no other status filter.
 * - Running and paused scans are pinned on top, in both tabs, with live progress from
 *   `GET /api/v1/scans/live`, polled only while anything is live.
 * - Filters: source, time, search, and "Clear filters (N hidden)" when one is on.
 * - By date (default): collapsible day groups, Today and Yesterday open, all open while a
 *   filter is on. By repository: a list on the left, that repository's mini dashboard on
 *   the right, its chart the full history whatever the filters.
 * - Archived (DEC-192): scans taken out of the default lists, and the records of scans
 *   deleted permanently. Archiving changes no trend, and the tab says so.
 *
 * - Who (DEC-196): Mine (scans I started), Shared (the workspace's), All I can see.
 * - "See why" opens the What happened pop-up in place (DEC-197).
 *
 * Every count and filter is server-side (`GET /api/v1/scans`), so nothing here loads
 * every scan to count it.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronRight } from 'lucide-react'
import { scansApi } from '../api/scans'
import { AppShell } from '../components/layout/AppShell'
import { resultsPath } from '../components/layout/reportViews'
import { PageTitle } from '../components/shared/PageTitle'
import { TonePill } from '../components/shared/Badge'
import { LiveScanCard } from '../components/scans/LiveScanCard'
import { RepoDashboard } from '../components/scans/RepoDashboard'
import { type DashItem, ScanRow, StatusDot } from '../components/scans/ScanRow'
import {
  DATE_BUCKETS,
  type DateBucket,
  agoLabel,
  dateBucket,
  pausedOfferSentence,
  repoLabel,
  sourceLabel,
  sourceOf,
} from '../components/scans/scanFormat'
import {
  SCAN_STATUS,
  STATUS_GROUPS,
  type ScanStatusKey,
  pendingStatus,
  statusOf,
} from '../components/scans/scanStatus'
import { useScanActions } from '../components/scans/useScanActions'
import { WhatHappenedPopup } from '../components/scans/WhatHappenedPopup'
import { useAuth } from '../hooks/useAuth'
import { LIVE_POLL_MS, SCANS_KEY, anythingLive, useLiveScans } from '../hooks/useScanQueries'
import type {
  LiveScanSummary,
  ScanListParams,
  ScanStatusGroup,
  ScanStatusResponse,
} from '../types/api'

const PAGE_SIZE = 50
const REPO_HISTORY_LIMIT = 100
const TAB_STORAGE_KEY = 'tdia.scans.tab'

type Tab = 'date' | 'repo' | 'archived'
type SourceFilter = 'any' | 'github' | 'upload'
type TimeFilter = 'any' | 'today' | 'week'
type WhoFilter = 'all' | 'mine' | 'shared'

function readTab(): Tab {
  try {
    const v = window.localStorage.getItem(TAB_STORAGE_KEY)
    return v === 'repo' || v === 'archived' ? v : 'date'
  } catch {
    return 'date'
  }
}

function writeTab(tab: Tab) {
  try {
    window.localStorage.setItem(TAB_STORAGE_KEY, tab)
  } catch {
    // A blocked store only costs the remembered tab.
  }
}

function sinceFor(time: TimeFilter): string | undefined {
  if (time === 'any') return undefined
  if (time === 'today') {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d.toISOString()
  }
  return new Date(Date.now() - 7 * 86_400_000).toISOString()
}

const DEFAULT_OPEN: Partial<Record<DateBucket, boolean>> = { Today: true, Yesterday: true }

const TILE_SUB: Record<ScanStatusGroup, string> = {
  in_progress: '',
  waiting: 'They start by themselves, in order. Nothing for you to do.',
  needs_a_look: 'Failed or finished with gaps. Each one says why.',
  finished: 'Finished scans you can open, re-run or delete.',
}

const SUMMARY_ORDER: ScanStatusKey[] = ['complete', 'partial', 'failed', 'stopped', 'queued', 'scheduled']

function chip(on: boolean): string {
  return `whitespace-nowrap rounded-full border px-3 py-[6px] text-[12px] font-semibold transition-colors ${
    on ? 'border-accent bg-accent text-white' : 'border-hairline-strong text-body hover:bg-hover-hue'
  }`
}

function liveAsScan(live: LiveScanSummary): ScanStatusResponse {
  // The actions need only these fields of a running or paused scan.
  return {
    scan_id: live.scan_id,
    state: live.state,
    repo_url: live.repo_url,
    branch: live.branch,
    started_at: live.started_at,
    use_llm: live.use_llm,
    failure_code: null,
    failure_is_retryable: false,
    finding_count: live.findings_so_far,
    findings_by_severity: {},
    archive_available_at: live.archive_available_at ?? null,
    archived_at: null,
    deleted_at: null,
  } as unknown as ScanStatusResponse
}

export default function ScansPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [story, setStory] = useState<ScanStatusResponse | null>(null)
  const { actionsFor, scanActions, againAction, notice, setNotice, profile } = useScanActions({ onSeeWhy: setStory })
  const { claims } = useAuth()
  const me = claims?.user_id ?? null

  const [tab, setTabState] = useState<Tab>(readTab)
  const [status, setStatus] = useState<ScanStatusGroup | null>(null)
  const [source, setSource] = useState<SourceFilter>('any')
  const [time, setTime] = useState<TimeFilter>('any')
  const [who, setWho] = useState<WhoFilter>('all')
  const [q, setQ] = useState('')
  const [open, setOpen] = useState<Partial<Record<DateBucket, boolean>>>({})
  const [repoPick, setRepoPick] = useState<string | null>(null)
  const [now, setNow] = useState(() => Date.now())

  const setTab = (t: Tab) => {
    setTabState(t)
    writeTab(t)
  }

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(id)
  }, [])

  const since = useMemo(() => sinceFor(time), [time])
  const query = q.trim()
  const hasFilter = status !== null || source !== 'any' || time !== 'any' || query !== '' || who !== 'all'

  const { data: live } = useLiveScans()
  const isLive = anythingLive(live)

  const params: ScanListParams = {
    status: status ?? undefined,
    source: source === 'any' ? undefined : source,
    date_from: since,
    q: query || undefined,
    visibility: who === 'all' ? undefined : who,
  }
  const list = useInfiniteQuery({
    queryKey: [...SCANS_KEY, 'list', params],
    queryFn: ({ pageParam }) => scansApi.list({ ...params, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, p) => n + p.scans.length, 0)
      return loaded < last.total ? loaded : undefined
    },
    // DEC-191 decision 5: lists poll while anything is live.
    refetchInterval: isLive ? LIVE_POLL_MS : false,
    retry: false,
  })
  // DEC-192: the Archived tab. The status numbers describe the main lists, so only the
  // source, time and search filters apply here.
  const archivedParams: ScanListParams = {
    archived: 'only',
    source: params.source,
    date_from: params.date_from,
    q: params.q,
    visibility: params.visibility,
  }
  const archivedList = useInfiniteQuery({
    queryKey: [...SCANS_KEY, 'list', archivedParams],
    queryFn: ({ pageParam }) => scansApi.list({ ...archivedParams, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, p) => n + p.scans.length, 0)
      return loaded < last.total ? loaded : undefined
    },
    enabled: tab === 'archived',
    retry: false,
  })
  const pendingQuery = useQuery({
    queryKey: [...SCANS_KEY, 'pending'],
    queryFn: () => scansApi.pending(),
    refetchInterval: isLive ? LIVE_POLL_MS : false,
    retry: false,
  })

  // A scan that leaves the live summary has finished, stopped or failed: refresh every
  // list now, rather than when the next page is opened (DEC-191 decision 5).
  const liveIds = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!live) return
    const next = new Set(live.scans.map((s) => s.scan_id))
    const ended = [...liveIds.current].some((id) => !next.has(id))
    liveIds.current = next
    if (ended) void queryClient.invalidateQueries({ queryKey: SCANS_KEY })
  }, [live, queryClient])

  const pages = list.data?.pages ?? []
  const firstPage = pages[0]
  const scans = pages.flatMap((p) => p.scans)
  const total = firstPage?.total ?? 0
  const counts = firstPage?.status_counts
  const pendingAll = pendingQuery.data?.requests ?? []

  // Client-side checks for the two sources the list endpoint does not hold: the live
  // summary and deferred requests. Same rules as the server's filters.
  const passesWho = (s: { visibility?: string | null; created_by_user_id?: string | null; owned_by_viewer?: boolean }) => {
    if (who === 'mine') return s.owned_by_viewer ?? (!!me && s.created_by_user_id === me)
    if (who === 'shared') return (s.visibility ?? 'workspace') === 'workspace'
    return true
  }
  const passesClient = (repoUrl: string, at: string) => {
    if (source !== 'any' && sourceOf(repoUrl) !== source) return false
    if (since && new Date(at).getTime() < new Date(since).getTime()) return false
    if (query && !repoUrl.toLowerCase().includes(query.toLowerCase())) return false
    return true
  }

  const pinned = (live?.scans ?? [])
    .map((l) => ({ live: l, status: statusOf({ state: l.state, failure_code: null }) }))
    .filter((x) => x.status === 'running' || x.status === 'paused')
    .filter((x) => (status === null || status === 'in_progress') && passesClient(x.live.repo_url, x.live.started_at))
    .filter((x) => passesWho(x.live))

  const pendingShown = pendingAll.filter(
    (p) => (status === null || status === 'waiting') && passesClient(p.repo_url, p.requested_at) && passesWho(p),
  )

  const dateItems: DashItem[] = [
    ...scans
      .map((s) => ({ kind: 'scan' as const, id: s.scan_id, status: statusOf(s), at: s.started_at, scan: s }))
      .filter((x) => SCAN_STATUS[x.status].group !== 'in_progress'),
    ...pendingShown.map((p) => ({
      kind: 'pending' as const,
      id: p.request_id,
      status: pendingStatus(p.run_after, now),
      at: p.requested_at,
      pending: p,
    })),
  ].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())

  const archivedCount = firstPage?.archived_count ?? 0
  const archivedScans = (archivedList.data?.pages ?? []).flatMap((p) => p.scans)
  const archivedTotal = archivedList.data?.pages[0]?.total ?? 0
  const archivedItems: DashItem[] = archivedScans.map((s) => ({
    kind: 'scan' as const,
    id: s.scan_id,
    status: statusOf(s),
    at: s.started_at,
    scan: s,
  }))

  const totalAll = (firstPage?.total_all ?? 0) + pendingAll.length
  const shownCount = total + pendingShown.length
  const hidden = Math.max(0, totalAll - shownCount)

  const loadedAll = !list.isLoading && !pendingQuery.isLoading
  const emptyWorkspace =
    loadedAll && !hasFilter && totalAll === 0 && pinned.length === 0 && archivedCount === 0
  // The Archived tab answers for itself, so "no match" is about the main lists only.
  const noMatch = loadedAll && hasFilter && shownCount === 0 && pinned.length === 0 && tab !== 'archived'


  const clearAll = () => {
    setStatus(null)
    setSource('any')
    setTime('any')
    setWho('all')
    setQ('')
  }

  // ---- By date ----
  const groups = DATE_BUCKETS.map((bucket) => ({
    bucket,
    items: dateItems.filter((i) => dateBucket(i.at, now) === bucket),
  })).filter((g) => g.items.length > 0)
  const isOpen = (b: DateBucket) => open[b] ?? (hasFilter ? true : !!DEFAULT_OPEN[b])
  const setAllOpen = (v: boolean) => setOpen(Object.fromEntries(DATE_BUCKETS.map((b) => [b, v])))

  // ---- By repository ----
  const repos = useMemo(() => {
    const map = new Map<string, ScanStatusResponse[]>()
    for (const s of scans) {
      const list = map.get(s.repo_url) ?? []
      list.push(s)
      map.set(s.repo_url, list)
    }
    return [...map.entries()].map(([url, items]) => ({ url, items }))
  }, [scans])
  const selectedRepo = repos.find((r) => r.url === repoPick)?.url ?? repos[0]?.url ?? null
  const history = useQuery({
    queryKey: [...SCANS_KEY, 'repo', selectedRepo],
    queryFn: () => scansApi.list({ repo: selectedRepo!, limit: REPO_HISTORY_LIMIT }),
    enabled: tab === 'repo' && !!selectedRepo,
    refetchInterval: isLive ? LIVE_POLL_MS : false,
    retry: false,
  })

  // DEC-196: another member's private scan can hold a slot; say so without naming it.
  const hiddenSlots = live?.hidden_private_in_flight ?? 0
  const inProgressSub = live
    ? `${live.running} running, ${live.paused} paused${live.paused ? ' and waiting on you' : ''}.${
        hiddenSlots > 0 ? ` ${hiddenSlots} ${hiddenSlots === 1 ? 'slot is' : 'slots are'} used by a private scan.` : ''
      }`
    : 'Running and paused scans.'
  const tileCount = (g: ScanStatusGroup): string => {
    if (!counts) return '...'
    return String(counts[g] + (g === 'waiting' ? pendingAll.length : 0))
  }

  return (
    <AppShell crumb="Scans" active="scans">
      <main className="mx-auto flex w-full max-w-[1180px] flex-col gap-[18px] px-4 pb-20 pt-7 md:px-7">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-col gap-1">
            <PageTitle eyebrow="Scans">Every scan, in one place</PageTitle>
            <p className="mt-1 text-[14px] text-muted">
              Running scans are on top with their progress. Everything else is below, by date or by repository.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/connect')}
            className="whitespace-nowrap rounded-md bg-accent px-[18px] py-[10px] text-[13px] font-semibold text-white hover:bg-[#6A4AAB]"
          >
            + New scan
          </button>
        </div>

        {notice && (
          <div
            role="status"
            className={`flex items-center gap-3 rounded-md border px-4 py-3 text-[13px] ${
              notice.tone === 'error' ? 'border-critical/30 bg-chip-critical text-critical' : 'border-hairline bg-panel text-ink'
            }`}
          >
            <span className="flex-1">{notice.text}</span>
            <button type="button" onClick={() => setNotice(null)} className="text-[12px] font-semibold text-muted hover:text-ink">
              Close
            </button>
          </div>
        )}

        {list.isError && (
          <div className="rounded-md border border-critical/30 bg-chip-critical px-4 py-3 text-[13px] text-critical">
            The scans could not be loaded. {list.error instanceof Error ? list.error.message : ''}
          </div>
        )}

        {!emptyWorkspace && (
          <>
            <div data-testid="status-tiles" className="grid grid-cols-2 overflow-hidden rounded-lg border border-hairline bg-panel shadow-card md:grid-cols-4">
              {STATUS_GROUPS.map((g, i) => {
                const on = status === g.id
                return (
                  <button
                    key={g.id}
                    type="button"
                    aria-pressed={on}
                    data-testid={`tile-${g.id}`}
                    onClick={() => setStatus(on ? null : g.id)}
                    className={`flex flex-col gap-[6px] px-[18px] py-4 text-left text-ink transition-colors hover:bg-hover-hue ${
                      i > 0 ? 'md:border-l md:border-hairline' : ''
                    } ${on ? 'bg-fill-subtle shadow-[inset_0_-3px_0_rgb(var(--accent))]' : ''}`}
                  >
                    <span className="flex items-center gap-2">
                      <StatusDot status={g.dotKey} size={9} />
                      <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted">{g.label}</span>
                    </span>
                    <span className="tabular-nums text-[40px] font-medium leading-[1.1] text-ink">{tileCount(g.id)}</span>
                    <span className="text-[12px] leading-[1.45] text-body">
                      {on ? 'Showing only these. Click again to show every scan.' : g.id === 'in_progress' ? inProgressSub : TILE_SUB[g.id]}
                    </span>
                  </button>
                )
              })}
            </div>

            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-hairline bg-panel px-4 py-3 shadow-card">
              <span className="text-[12px] font-semibold text-muted">Source</span>
              {(
                [
                  ['any', 'Any source'],
                  ['github', 'GitHub'],
                  ['upload', 'Uploaded folders'],
                ] as [SourceFilter, string][]
              ).map(([v, label]) => (
                <button key={v} type="button" aria-pressed={source === v} onClick={() => setSource(v)} className={chip(source === v)}>
                  {label}
                </button>
              ))}
              <span className="ml-3 text-[12px] font-semibold text-muted">When</span>
              {(
                [
                  ['any', 'Any time'],
                  ['today', 'Today'],
                  ['week', 'Last 7 days'],
                ] as [TimeFilter, string][]
              ).map(([v, label]) => (
                <button key={v} type="button" aria-pressed={time === v} onClick={() => setTime(v)} className={chip(time === v)}>
                  {label}
                </button>
              ))}
              <span className="ml-3 text-[12px] font-semibold text-muted">Who</span>
              {(
                [
                  ['all', 'All'],
                  ['mine', 'Mine'],
                  ['shared', 'Shared'],
                ] as [WhoFilter, string][]
              ).map(([v, label]) => (
                <button key={v} type="button" aria-pressed={who === v} onClick={() => setWho(v)} className={chip(who === v)}>
                  {label}
                </button>
              ))}
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                aria-label="Find a repository or folder"
                placeholder="Find a repository"
                className="ml-3 min-w-[120px] max-w-[260px] flex-[1_1_160px] rounded-md border border-control bg-ground px-3 py-[7px] text-[13px] text-ink placeholder-faint focus:outline-none focus-visible:shadow-focus"
              />
              <span className="ml-auto">
                {hasFilter && (
                  <button
                    type="button"
                    onClick={clearAll}
                    className="whitespace-nowrap rounded-full border border-accent bg-accent/[0.14] px-[13px] py-[6px] text-[12px] font-semibold text-accent-text"
                  >
                    {hidden > 0 ? `Clear filters (${hidden} hidden)` : 'Clear filters'}
                  </button>
                )}
              </span>
            </div>
          </>
        )}

        {emptyWorkspace && (
          <div data-testid="empty-workspace" className="flex flex-col items-center gap-[10px] rounded-lg border border-hairline bg-panel px-6 py-12 text-center shadow-card">
            <span className="text-[20px] font-semibold text-ink">No scans yet</span>
            <span className="max-w-[520px] text-[14px] leading-[1.55] text-body">
              A scan reads a repository and reports its technical debt. Start one by connecting GitHub, uploading a folder
              from your computer, or pasting a link. Running scans show here with their progress, and finished ones stay
              here so you can open, re-run or delete them.
            </span>
            <button
              type="button"
              onClick={() => navigate('/connect')}
              className="rounded-md bg-accent px-[18px] py-[10px] text-[13px] font-semibold text-white hover:bg-[#6A4AAB]"
            >
              Start your first scan
            </button>
          </div>
        )}

        {noMatch && (
          <div data-testid="no-match" className="flex flex-col items-center gap-2 rounded-lg border border-hairline bg-panel px-6 py-9 text-center shadow-card">
            <span className="text-[17px] font-semibold text-ink">No scans match these filters</span>
            <span className="text-[14px] text-body">
              {hidden} {hidden === 1 ? 'scan is' : 'scans are'} hidden by the filters you picked.
            </span>
            <button
              type="button"
              onClick={clearAll}
              className="rounded-md border border-hairline-strong px-4 py-[9px] text-[13px] font-semibold text-ink hover:bg-hover-hue"
            >
              Clear filters
            </button>
          </div>
        )}

        {!emptyWorkspace && !noMatch && (
          <>
            {pinned.length > 0 && (
              <section className="flex flex-col gap-[10px]" aria-label="In progress now">
                <span className="flex flex-wrap items-baseline gap-[10px]">
                  <span className="text-[17px] font-semibold text-ink">In progress now</span>
                  <span className="text-[13px] text-muted">Always on top, in both tabs. Counts while running are before review.</span>
                </span>
                <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(320px,1fr))]">
                  {pinned.map((x) => (
                    <LiveScanCard
                      key={x.live.scan_id}
                      live={x.live}
                      status={x.status}
                      actions={scanActions(liveAsScan(x.live), x.status)}
                      note={x.status === 'paused' ? pausedOfferSentence(liveAsScan(x.live), now) : undefined}
                      now={now}
                    />
                  ))}
                </div>
              </section>
            )}

            <div role="tablist" aria-label="How to show your scans" className="mt-[6px] flex flex-wrap items-end gap-1 border-b border-hairline">
              {(
                [
                  ['date', 'By date', `${dateItems.length}${list.hasNextPage ? '+' : ''}`],
                  ['repo', 'By repository', String(repos.length)],
                  ['archived', 'Archived', String(archivedCount)],
                ] as [Tab, string, string][]
              ).map(([id, label, n]) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={tab === id}
                  onClick={() => setTab(id)}
                  className={`-mb-px border-b-2 px-4 py-[10px] text-[14px] font-semibold transition-colors ${
                    tab === id ? 'border-accent text-ink' : 'border-transparent text-faint hover:text-ink'
                  }`}
                >
                  {label} <span className="tabular-nums text-[12px] font-medium opacity-75">{n}</span>
                </button>
              ))}
              <span className="ml-auto flex items-center gap-[6px] pb-2">
                {tab === 'date' ? (
                  <>
                    <button type="button" onClick={() => setAllOpen(true)} className="px-1 text-[12px] font-semibold text-accent-text">
                      Open all
                    </button>
                    <span className="text-faint">·</span>
                    <button type="button" onClick={() => setAllOpen(false)} className="px-1 text-[12px] font-semibold text-accent-text">
                      Close all
                    </button>
                  </>
                ) : tab === 'repo' ? (
                  <span className="text-[12px] text-muted">Each repository&apos;s chart shows its whole history, whatever the filters.</span>
                ) : (
                  <span className="text-[12px] text-muted">Archived scans keep everything. Trends and the Debt Index still count them.</span>
                )}
              </span>
            </div>

            {tab === 'date' && (
              <div role="tabpanel" className="flex flex-col gap-[18px]">
                {groups.map(({ bucket, items }) => {
                  const o = isOpen(bucket)
                  const att = items.filter((i) => SCAN_STATUS[i.status].group === 'needs_a_look').length
                  const summary = SUMMARY_ORDER.map((k) => {
                    const n = items.filter((i) => i.status === k).length
                    return n ? `${n} ${SCAN_STATUS[k].word.toLowerCase()}` : ''
                  })
                    .filter(Boolean)
                    .join(', ')
                  return (
                    <section key={bucket} data-testid={`group-${bucket}`} className="overflow-hidden rounded-lg border border-hairline bg-panel shadow-card">
                      <button
                        type="button"
                        aria-expanded={o}
                        onClick={() => setOpen((prev) => ({ ...prev, [bucket]: !o }))}
                        className={`flex w-full flex-wrap items-center gap-[10px] px-[18px] py-[14px] text-left text-ink ${o ? 'border-b border-hairline' : ''}`}
                      >
                        <ChevronRight className={`h-4 w-4 flex-none text-accent-text transition-transform ${o ? 'rotate-90' : ''}`} />
                        <span className="text-[15px] font-semibold">{bucket}</span>
                        <span className="text-[13px] text-muted">
                          {items.length} {items.length === 1 ? 'scan' : 'scans'}
                          {summary ? ` · ${summary}` : ''}
                        </span>
                        {!o && att > 0 && (
                          <TonePill tone="warn">
                            {att} {att === 1 ? 'needs' : 'need'} a look
                          </TonePill>
                        )}
                        <span className="ml-auto text-[12px] font-semibold text-accent-text">{o ? 'Close' : 'Open'}</span>
                      </button>
                      {o && items.map((item) => <ScanRow key={item.id} item={item} actions={actionsFor(item)} now={now} />)}
                    </section>
                  )
                })}
                {list.hasNextPage ? (
                  <button
                    type="button"
                    onClick={() => void list.fetchNextPage()}
                    disabled={list.isFetchingNextPage}
                    className="mx-auto rounded-md border border-hairline-strong px-4 py-[9px] text-[13px] font-semibold text-ink hover:bg-hover-hue disabled:opacity-50"
                  >
                    {list.isFetchingNextPage ? 'Loading...' : `Load more (${Math.max(0, total - scans.length)} more)`}
                  </button>
                ) : (
                  groups.length > 0 && <p className="text-center text-[12px] text-faint">That is every scan that matches.</p>
                )}
              </div>
            )}

            {tab === 'archived' && (
              <div role="tabpanel" aria-label="Archived scans" className="flex flex-col gap-[14px]">
                <p data-testid="archive-trend-note" className="text-[13px] leading-[1.5] text-body">
                  Archiving only takes a scan out of the lists, the four numbers and the sidebar. It does not change trends or
                  the Debt Index. A scan deleted permanently stays here as a record, and its point is left out of trends, which
                  say how many.
                </p>
                {archivedList.isError && (
                  <div className="rounded-md border border-critical/30 bg-chip-critical px-4 py-3 text-[13px] text-critical">
                    The archived scans could not be loaded.{' '}
                    {archivedList.error instanceof Error ? archivedList.error.message : ''}
                  </div>
                )}
                {archivedList.isLoading ? (
                  <p className="text-[13px] text-muted">Loading...</p>
                ) : archivedItems.length === 0 ? (
                  <div data-testid="archived-empty" className="rounded-lg border border-hairline bg-panel px-6 py-9 text-center text-[14px] text-body shadow-card">
                    {hasFilter
                      ? 'No archived scans match these filters.'
                      : 'Nothing is archived. Archive a finished or failed scan to take it out of the lists without losing it.'}
                  </div>
                ) : (
                  <section className="overflow-hidden rounded-lg border border-hairline bg-panel shadow-card">
                    {archivedItems.map((item) => (
                      <ScanRow key={item.id} item={item} actions={actionsFor(item)} now={now} />
                    ))}
                  </section>
                )}
                {archivedList.hasNextPage && (
                  <button
                    type="button"
                    onClick={() => void archivedList.fetchNextPage()}
                    disabled={archivedList.isFetchingNextPage}
                    className="mx-auto rounded-md border border-hairline-strong px-4 py-[9px] text-[13px] font-semibold text-ink hover:bg-hover-hue disabled:opacity-50"
                  >
                    {archivedList.isFetchingNextPage
                      ? 'Loading...'
                      : `Load more (${Math.max(0, archivedTotal - archivedScans.length)} more)`}
                  </button>
                )}
              </div>
            )}

            {tab === 'repo' && (
              <div role="tabpanel" className="grid items-start gap-4 md:grid-cols-[280px_minmax(0,1fr)]">
                <div className="overflow-hidden rounded-lg border border-hairline bg-panel shadow-card">
                  <div className="flex flex-col gap-[2px] border-b border-hairline px-4 py-3">
                    <span className="text-[14px] font-semibold text-ink">
                      Repositories <span className="tabular-nums text-[12px] font-medium text-muted">{repos.length}</span>
                    </span>
                    <span className="text-[12px] text-muted">Most recent activity first.</span>
                  </div>
                  {repos.map((r) => {
                    const s0 = r.items[0]
                    const st = statusOf(s0)
                    const on = r.url === selectedRepo
                    return (
                      <button
                        key={r.url}
                        type="button"
                        aria-current={on}
                        data-testid="repo-item"
                        onClick={() => setRepoPick(r.url)}
                        className={`flex w-full items-start gap-[10px] border-b border-l-[3px] border-b-hairline py-[11px] pl-[13px] pr-[14px] text-left text-ink last:border-b-0 ${
                          on ? 'border-l-accent bg-fill-subtle' : 'border-l-transparent hover:bg-hover-hue'
                        }`}
                      >
                        <span className="mt-[5px]"><StatusDot status={st} size={8} /></span>
                        <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
                          <span className="flex items-baseline gap-2">
                            <span className="truncate text-[14px] font-semibold">{repoLabel(r.url)}</span>
                            <span className="ml-auto whitespace-nowrap tabular-nums text-[12px] text-faint">
                              {r.items.length} {r.items.length === 1 ? 'scan' : 'scans'}
                            </span>
                          </span>
                          <span className="text-[11px] text-muted">
                            {sourceOf(r.url) === 'upload' ? 'Uploaded folder, grouped by name' : sourceLabel(r.url, s0.branch)}
                          </span>
                          <span className={`text-[12px] font-semibold ${SCAN_STATUS[st].text}`}>
                            {SCAN_STATUS[st].word} <span className="font-normal text-muted">· {agoLabel(s0.started_at, now)}</span>
                          </span>
                          {hasFilter && <span className="text-[11px] text-accent-text">Counting scans that match your filters</span>}
                        </span>
                      </button>
                    )
                  })}
                  {repos.length === 0 && <p className="px-4 py-6 text-[13px] text-muted">No repositories in this view.</p>}
                </div>
                {selectedRepo && (
                  <RepoDashboard
                    key={selectedRepo}
                    repoUrl={selectedRepo}
                    history={history.data?.scans ?? []}
                    totalInHistory={history.data?.total ?? 0}
                    loading={history.isLoading}
                    scanActions={scanActions}
                    againAction={againAction}
                    openResults={(s) => navigate(resultsPath(profile, s.scan_id))}
                    now={now}
                  />
                )}
              </div>
            )}
          </>
        )}
      </main>
      {story && <WhatHappenedPopup scan={story} onClose={() => setStory(null)} />}
    </AppShell>
  )
}
